import useTranslation from 'next-translate/useTranslation';
import type { ComponentPropsWithoutRef } from 'react';

import type { Channel } from '~/domains/commissioner/interfaces/channel.interface';
import { Button, ButtonLink } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import dt from '~/testing';

import { formatChannelDetails } from '../../utils/formatChannelDetails';

import { ChannelHeader } from './ChannelHeader';
import { useShareChannel } from './useShareMyChannel';
import { useFollowChannel } from './useFollowChannel';

type ConditionalButtonLinkProps = ComponentPropsWithoutRef<typeof ButtonLink> & {
  condition: boolean;
};

function ConditionalButtonLink({ condition, children, ...props }: ConditionalButtonLinkProps) {
  if (!condition) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <ButtonLink {...props}>{children}</ButtonLink>;
}

type ConnectedChannelHeaderProps = {
  channel: Channel;
  isCurrentUserChannel?: boolean;
  isLoggedIn?: boolean;
};

export function ConnectedChannelHeader({
  channel,
  isCurrentUserChannel,
  isLoggedIn,
}: ConnectedChannelHeaderProps) {
  const { t } = useTranslation('channel');
  const isMobile = useIsMobile();

  const { handleShareMyChannel } = useShareChannel();
  const { handleFollow, isFollowLoading, handleUnfollow, isUnfollowLoading } = useFollowChannel({
    channelId: channel.id,
    handle: channel.handle,
    location: 'channel_detail',
  });

  return (
    <ChannelHeader>
      <ChannelHeader.Banner src={channel.backdropUrl} />
      <ChannelHeader.Content>
        <ChannelHeader.Header>
          <ChannelHeader.Avatar src={channel.avatarUrl} handle={channel.handle} />

          {!channel.isSplashAccount && (
            <ChannelHeader.Numbers>
              <ChannelHeader.NumbersItem>
                <ChannelHeader.Number
                  data-test-id={dt.channel.connectedChannelHeader.channelEntriesCount}
                >
                  {formatChannelDetails(channel.entriesCount)}
                </ChannelHeader.Number>
                <ChannelHeader.NumberLabel>{t('header.numbers.entries')}</ChannelHeader.NumberLabel>
              </ChannelHeader.NumbersItem>

              <ChannelHeader.NumbersDivider />

              <ConditionalButtonLink
                data-test-id={dt.channel.connectedChannelHeader.followersButton}
                condition={isLoggedIn}
                href={Routes.channelSearch(channel.handle, URLQueryKeys.CHANNEL_FOLLOWERS)}
                variant="text"
              >
                <ChannelHeader.NumbersItem>
                  <ChannelHeader.Number
                    data-test-id={dt.channel.connectedChannelHeader.channelFollowersCount}
                  >
                    {formatChannelDetails(channel.followersCount)}
                  </ChannelHeader.Number>
                  <ChannelHeader.NumberLabel>
                    {t('header.numbers.followers')}
                  </ChannelHeader.NumberLabel>
                </ChannelHeader.NumbersItem>
              </ConditionalButtonLink>

              <ChannelHeader.NumbersDivider />

              <ConditionalButtonLink
                data-test-id={dt.channel.connectedChannelHeader.followingButton}
                condition={isLoggedIn}
                href={Routes.channelSearch(channel.handle, URLQueryKeys.CHANNEL_FOLLOWING)}
                variant="text"
              >
                <ChannelHeader.NumbersItem>
                  <ChannelHeader.Number
                    data-test-id={dt.channel.connectedChannelHeader.channelFollowingCount}
                  >
                    {formatChannelDetails(channel.followingCount)}
                  </ChannelHeader.Number>
                  <ChannelHeader.NumberLabel>
                    {t('header.numbers.following')}
                  </ChannelHeader.NumberLabel>
                </ChannelHeader.NumbersItem>
              </ConditionalButtonLink>
            </ChannelHeader.Numbers>
          )}
        </ChannelHeader.Header>

        <ChannelHeader.Info>
          {channel.name && (
            <ChannelHeader.Name
              endAdornment={
                <>
                  {!isCurrentUserChannel && channel.followsMe && (
                    <ChannelHeader.FollowsCurrentUserTag>
                      {t('header.followsYouTag')}
                    </ChannelHeader.FollowsCurrentUserTag>
                  )}
                  {(channel.verified || channel.isSplashAccount) && <ChannelHeader.VerifiedBadge />}
                </>
              }
            >
              {channel.name}
            </ChannelHeader.Name>
          )}
          <ChannelHeader.Handle>{channel.handle}</ChannelHeader.Handle>
          <ChannelHeader.Description>{channel.description}</ChannelHeader.Description>
        </ChannelHeader.Info>

        <ChannelHeader.Actions>
          {(() => {
            if (channel.isSplashAccount) {
              return (
                <Button variant="tertiary-fill" size="medium" onClick={handleShareMyChannel}>
                  {isMobile ? t('header.shareButton') : t('header.copyLink')}
                </Button>
              );
            }

            if (isLoggedIn) {
              if (isCurrentUserChannel) {
                return (
                  <>
                    <ButtonLink
                      data-test-id={dt.channel.connectedChannelHeader.editButton}
                      href={Routes.channelEdit(channel.handle)}
                      variant="tertiary-fill"
                      size="medium"
                    >
                      {t('header.editButton')}
                    </ButtonLink>
                    <Button
                      variant="tertiary-fill"
                      size="medium"
                      onClick={handleShareMyChannel}
                      data-test-id={dt.channel.connectedChannelHeader.copyLinkButton}
                    >
                      {isMobile ? t('header.shareButton') : t('header.copyLink')}
                    </Button>
                  </>
                );
              }

              return (
                <Button
                  data-test-id={dt.channel.connectedChannelHeader.followButton}
                  variant={channel.isFollowed ? 'tertiary-fill' : 'secondary-fill'}
                  size="medium"
                  disabled={isFollowLoading || isUnfollowLoading}
                  onClick={() => {
                    if (channel.isFollowed) {
                      handleUnfollow();
                    } else {
                      handleFollow();
                    }
                  }}
                >
                  {channel.isFollowed ? t('header.followingButton') : t('header.followButton')}
                </Button>
              );
            }
          })()}
        </ChannelHeader.Actions>
      </ChannelHeader.Content>
    </ChannelHeader>
  );
}
